import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Grid,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Switch,
	FormControlLabel,
	Box,
	CircularProgress,
	Alert,
} from "@mui/material";
import {
	SettingManagement,
	CreateSettingPayload,
	UpdateSettingPayload,
} from "../../../domain/settings.interface";

interface SettingsModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (data: CreateSettingPayload | UpdateSettingPayload) => Promise<void>;
	editingSetting?: SettingManagement | null;
	isAccountView?: boolean;
	selectedAccountName?: string;
}

interface FormData {
	product: "sims";
	application: "mobile" | "backoffice";
	module: string;
	section: string;
	name: string;
	description: string;
	type: "string" | "number" | "boolean" | "object" | "array";
	default: string;
	active: boolean;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
	open,
	onClose,
	onSave,
	editingSetting,
	isAccountView = false,
	selectedAccountName,
}) => {
	const [isLoading, setIsLoading] = React.useState(false);

	// Determine if we're editing an account override
	const isEditingAccountOverride =
		isAccountView && editingSetting?.accountOverride;

	// Helper function to safely display default value
	const formatDefaultValue = (value: string): string => {
		if (typeof value === "object") {
			return JSON.stringify(value);
		}
		return String(value);
	};

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<FormData>({
		defaultValues: {
			product: "sims",
			application: "backoffice",
			module: "",
			section: "",
			name: "",
			description: "",
			type: "string",
			default: "",
			active: true,
		},
	});

	const typeValue = watch("type");

	// Populate form when editing
	useEffect(() => {
		if (editingSetting && open) {
			// If editing an account override, use the override value
			const valueToEdit = isEditingAccountOverride
				? editingSetting.accountOverride?.value
				: editingSetting.default;

			reset({
				product: editingSetting.product,
				application: editingSetting.application,
				module: editingSetting.module,
				section: editingSetting.section,
				name: editingSetting.name,
				description: editingSetting.description,
				type: editingSetting.type,
				default:
					typeof valueToEdit === "object"
						? JSON.stringify(valueToEdit)
						: String(valueToEdit),
				active: isEditingAccountOverride
					? editingSetting.accountOverride?.active ?? true
					: editingSetting.active,
			});
		} else if (!open) {
			reset({
				product: "sims",
				application: "backoffice",
				module: "",
				section: "",
				name: "",
				description: "",
				type: "string",
				default: "",
				active: true,
			});
		}
	}, [editingSetting, open, reset, isEditingAccountOverride]);

	const onSubmit = async (data: FormData) => {
		setIsLoading(true);
		try {
			// Send default value as string - backend will handle conversions
			const payload = {
				product: data.product,
				application: data.application,
				module: data.module,
				section: data.section,
				name: data.name,
				description: data.description,
				type: data.type,
				default: data.default, // Always send as string
				active: data.active,
			};

			await onSave(payload);
		} catch (error) {
			// Error handled by React Query
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{editingSetting
					? isEditingAccountOverride
						? `Edit Account Override - ${selectedAccountName}`
						: `Edit Setting${isAccountView ? ` - Create Override for ${selectedAccountName}` : ""}`
					: isAccountView
						? `Create Account Override - ${selectedAccountName}`
						: "Create New Setting"}
			</DialogTitle>
			<DialogContent>
				{isLoading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						minHeight={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box component="form" sx={{ mt: 2 }}>
						{isAccountView && (
							<Alert severity="info" sx={{ mb: 2 }}>
								{isEditingAccountOverride
									? `You are editing the account-specific override for ${selectedAccountName}. This will only affect this account.`
									: `You are creating an account-specific override for ${selectedAccountName}. This will override the global default value for this account only.`}
							</Alert>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<Controller
									name="application"
									control={control}
									rules={{ required: "Application is required" }}
									render={({ field }) => (
										<FormControl
											fullWidth
											error={!!errors.application}
											disabled={isAccountView}
										>
											<InputLabel>Application</InputLabel>
											<Select {...field} label="Application">
												<MenuItem value="mobile">Mobile</MenuItem>
												<MenuItem value="backoffice">Backoffice</MenuItem>
											</Select>
										</FormControl>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="module"
									control={control}
									rules={{ required: "Module is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Module"
											fullWidth
											error={!!errors.module}
											helperText={errors.module?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="section"
									control={control}
									rules={{ required: "Section is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Section"
											fullWidth
											error={!!errors.section}
											helperText={errors.section?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="name"
									control={control}
									rules={{ required: "Name is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Name"
											fullWidth
											error={!!errors.name}
											helperText={errors.name?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12}>
								<Controller
									name="description"
									control={control}
									rules={{ required: "Description is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Description"
											fullWidth
											multiline
											rows={2}
											error={!!errors.description}
											helperText={errors.description?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="type"
									control={control}
									rules={{ required: "Type is required" }}
									render={({ field }) => (
										<FormControl
											fullWidth
											error={!!errors.type}
											disabled={isAccountView}
										>
											<InputLabel>Type</InputLabel>
											<Select {...field} label="Type">
												<MenuItem value="string">String</MenuItem>
												<MenuItem value="number">Number</MenuItem>
												<MenuItem value="boolean">Boolean</MenuItem>
												<MenuItem value="object">Object</MenuItem>
												<MenuItem value="array">Array</MenuItem>
											</Select>
										</FormControl>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="active"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={<Switch {...field} checked={field.value} />}
											label="Active"
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12}>
								<Controller
									name="default"
									control={control}
									rules={{ required: "Default value is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label={
												isAccountView
													? "Override Value (Account-Specific)"
													: "Default Value"
											}
											fullWidth
											multiline={
												typeValue === "object" || typeValue === "array"
											}
											rows={
												typeValue === "object" || typeValue === "array" ? 4 : 1
											}
											error={!!errors.default}
											helperText={
												errors.default?.message ||
												(typeValue === "object" || typeValue === "array"
													? "Enter valid JSON"
													: isAccountView && editingSetting
														? `Global default: ${formatDefaultValue(editingSetting.default)}`
														: "")
											}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit(onSubmit)}
					variant="contained"
					color="primary"
					disabled={isLoading}
				>
					{editingSetting ? "Update" : "Create"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SettingsModal;
